<template>
  <div>
    <v-card 
      style="position: relative; cursor: default;"
      class="ma-2"
      :ripple="false"
      @click="() => {if ($vuetify.breakpoint.smAndDown) expanded = true}"
    >
      <div class="py-3">
        <api-errors :errors="errors" />
        <loading-bar v-if="!!awaitingApiResponse" />
        <v-sparkline 
          :value="stemLabels"
          height="35px"
          line-width="0.25px"
          type="bar"
        >
          <template #label="item">
            {{ minutesToDisplay(stems[item.index]) }}
          </template>
        </v-sparkline>
      </div>
      <v-sparkline 
        v-if="!!(reservationCovers || []).length"
        :value="reservationsBinary"
        height="20px"
        line-width="0.25px"
        type="bar"
        class="reservation-data"
        :gradient="['rgb(230, 230, 230)']"
        style="opacity: 90%;"
      />
      <v-sparkline 
        v-if="!!Object.keys(selectedReservation).length"
        :value="selectedReservationBinary"
        height="20px"
        line-width="0.25px"
        type="bar"
        class="reservation-data"
        :gradient="['lightblue']"
        style="opacity: 90%;"
      />
    </v-card>
    <v-dialog 
      v-model="expanded"
      @click:outside="expanded = false"
    >
      <v-card 
        style="position: relative;"
        class="ma-2"
      >
        <v-card-title class="pa-0 mb-1">
          <v-toolbar 
            dark
            color="primary lighten-1"
          >
            <v-btn 
              icon
              class="mr-2" 
              @click="expanded = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <div class="title">
              {{ `Table: ${tableName}` }}
            </div>
          </v-toolbar>
        </v-card-title>
        <div class="py-3">
          <api-errors :errors="errors" />
          <loading-bar v-if="!!awaitingApiResponse" />
          <v-sparkline 
            :value="stemLabels"
            height="35px"
            line-width="0.25px"
            type="bar"
          >
            <template #label="item">
              {{ minutesToDisplay(stems[item.index]) }}
            </template>
          </v-sparkline>
        </div>
        <v-sparkline 
          v-if="!!(reservationCovers || []).length"
          :value="reservationsBinary"
          height="20px"
          line-width="0.25px"
          type="bar"
          class="reservation-data-expanded"
          :gradient="['rgb(230, 230, 230)']"
          style="opacity: 90%;"
        />
        <v-sparkline 
          v-if="!!Object.keys(selectedReservation).length"
          :value="selectedReservationBinary"
          height="20px"
          line-width="0.25px"
          type="bar"
          class="reservation-data-expanded"
          :gradient="['lightblue']"
          style="opacity: 90%;"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import floorPlanHelpers from '../mixins/floorPlanHelpers'
import floorPlanTimeHelpers from '../mixins/floorPlanTimeHelpers'
export default {
  name: 'TableScheduleChart',
  mixins: [floorPlanHelpers, floorPlanTimeHelpers],
  props: {
    locationId: Number,
    tableId: Number,
    tableName: String,
    mealPeriodId: Number,
    selectedReservation: {
      type: Object,
      default () {return {}}
    },
    date: [Number, String],
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 1440,
    },
    reservationLength: {
      type: Number,
      default: 30
    }
  },
  data () {
    return {
      reservations: [],
      expanded: false,
    }
  },
  computed: {
    stems () {
      let result = new Array()
      let gap = Math.round((this.max - this.min) / 5)
      for (let i = 0; i < 6; i += 1) {
        result.push(Math.min(this.min + Math.round(gap * i), this.max))
      }
      return result
    },
    stemLabels () {
      return this.stems?.map(() => 1) ?? [1]
    },
    reservationsBinary () {
      let fillBits = [1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
      let emptyBits = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0.00000001] // Error in v-sparkline when last element of array is 0...
      let result = new Array()
      for (let i = 0; i < 10; i++) {
        result.push(...emptyBits)
      }
      for (let i = 1; i <= 100; i++) {
        if (this.reservationCovers.find(r => {
          return parseInt(r.start) <= i && parseInt(r.end) >= i
        })) {
          result.push(...fillBits)
        } else {
          result.push(...emptyBits)
        }
      }
      for (let i = 0; i < 10; i++) {
        result.push(...emptyBits)
      }
      return result
    },
    selectedReservationBinary () {
      let fillBits = [1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
      let emptyBits = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0.00000001] // Error in v-sparkline when last element of array is 0...
      let result = new Array()
      for (let i = 0; i < 10; i++) {
        result.push(...emptyBits)
      }
      for (let i = 1; i <= 100; i++) {
        if (i >= this.selectedReservationCovers.start && i <= this.selectedReservationCovers.end) {
          result.push(...fillBits)
        } else {
          result.push(...emptyBits)
        }
      }
      for (let i = 0; i < 10; i++) {
        result.push(...emptyBits)
      }
      return result
    },
    range () {
      return this.max - this.min
    },
    reservationCovers () {
      return this.reservations.reduce((acc, r) => {
        if (r.id === this.selectedReservation.id) {
          return acc
        }
        const reservationTime = this.dbTimeToMinutes(r.time)?.[0] ?? 0
        acc.push({
          start: Math.round((reservationTime - this.min) / this.range * 100),
          end: Math.round((reservationTime + this.reservationLength - this.min) / this.range * 100)
        })
        return acc
      }, []) ?? []
    },
    selectedReservationCovers () {
      const reservationTime = this.dbTimeToMinutes(this.selectedReservation.time)?.[0] ?? 0
      return {
        start: Math.round((reservationTime - this.min) / this.range * 100),
        end: Math.round((reservationTime + this.reservationLength - this.min) / this.range * 100)
      }
    }
  },
  mounted () {
    this.loadChartData()
  },
  methods: {
    loadChartData () {
      this.awaitingApiResponse = true
      this.$ftAPI().get(this.addResourceErrorHandler({
        command: 'diningReservation',
        load: this.reservations,
        loading: this.isLoading,
        afterLoad: () => this.awaitingApiResponse = false,
        error: () => this.awaitingApiResponse = false,
        params: {
          filter_methods: {
            filterByTableAssignment: [this.tableId],
            filterMealPeriod: [this.mealPeriodId],
            filterByDate: [this.date],
            filterCancelled: [],
          }
        }
      }))
    },
  }
}
</script>

<style scoped>
.reservation-data {
  position: absolute;
  top: 50%;
  transform: translateY(-70%);
}
.reservation-data-expanded {
  position: absolute;
  top: 50%;
}
.v-card::before {
  background: none;
}
</style>